// src/pages/Products/ListedProducts.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "../../../components/ui/Card.jsx";
import Hero from "../../../components/ui/Hero.jsx";
import {
  MortgageProductTypeBadge,
  SectorBadge,
} from "../../../components/ui/TypeBadge.jsx";
import { Eye, Pencil, Trash2, RefreshCw, CheckSquare, Square, X } from "lucide-react";
import { ViewProductModal, EditProductModal, scheduleDatetimeLocalToFrappe } from "./ProductModals.jsx";

import {
  getMortgageProducts,
  getInvoiceFinanceProducts,
  getInvoiceFinanceProduct,
  updateMortgageProduct,
  updateInvoiceFinanceProduct,
  deleteMortgageProduct,
  deleteInvoiceFinanceProduct,
  resolveInvoiceFinanceDocname,
  isInvoiceFinanceProduct,
} from "../../../api/lenderPortal/listedproducts";
import { applyListSelection } from "../../../utils/rangeSelect";

const HERO_PRODUCT_OPTIONS = [
  { value: "btl", label: "Buy-to-Let" },
  { value: "cbtl", label: "Complex BTL" },
  { value: "if", label: "Invoice Finance" },
];

/** Mortgage Product lines (BTL + Complex BTL) and their backend product_category. */
const MORTGAGE_LINE_CATEGORY = { btl: "BTL", cbtl: "CBTL" };

/** Complex BTL rows: category "CBTL" or a BTX- document ID. */
function isCbtlRow(row) {
  const cat = String(
    row?._raw?.category || row?._raw?.product_category || row?.category || ""
  ).trim().toLowerCase();
  if (cat === "cbtl") return true;
  const id = String(row?._id || row?._raw?.name || "").trim().toUpperCase();
  return id.startsWith("BTX-");
}

/** Custom chevron for the sort select so we can inset it slightly from the right edge */
const SORT_SELECT_CHEVRON_BG = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>'
)}")`;

const norm = (s) => String(s || "").toLowerCase();
const normalizeStatus = (status) => {
  const raw = String(status || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "activate" || raw === "active" || raw === "live" || raw === "approved") {
    return "active";
  }
  if (raw === "deactivate" || raw === "deactivated" || raw === "inactive") {
    return "deactivated";
  }
  if (raw === "scheduled" || raw === "schedule") {
    return "scheduled";
  }
  return raw;
};

const CenteredStatusPill = ({ status }) => {
  const s = normalizeStatus(status);
  const label =
    s === "active"
      ? "Active"
      : s === "deactivated"
        ? "Deactivated"
        : s === "scheduled"
          ? "Scheduled"
          : String(status || "—");
  const classes = {
    active: "bg-emerald-100 text-emerald-800",
    deactivated: "bg-rose-100 text-rose-800",
    scheduled: "bg-amber-100 text-amber-800",
  }[s] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold ${classes}`}
    >
      {label}
    </span>
  );
};

const formatScheduledDateTime = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  // Backend typically sends "YYYY-MM-DD HH:mm:ss" -> show without seconds.
  return raw.replace(/(\d{2}:\d{2}):\d{2}$/, "$1");
};

const getProductExpiryRaw = (product) =>
  product?.expiryDate ||
  product?.expiry_date ||
  product?.product_expiry_date ||
  product?.product_expiray_date ||
  product?._raw?.expiry_date ||
  product?._raw?.product_expiry_date ||
  product?._raw?.product_expiray_date ||
  "";

const toDateSafe = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  // Handle "YYYY-MM-DD HH:mm:ss" reliably in browser Date parsing.
  const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isExpiringWithin7Days = (value) => {
  const expiry = toDateSafe(value);
  if (!expiry) return false;
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return diffMs >= 0 && diffMs <= sevenDaysMs;
};

/* ----- loan range parsing ----- */
function parseLoanValue(input) {
  if (!input) return 0;

  let s = String(input).trim().toLowerCase();
  s = s.replace(/£|,/g, "").trim();

  const unitMatch = s.match(/^([\d.]+)\s*([km])$/);
  if (unitMatch) {
    const num = parseFloat(unitMatch[1]);
    const unit = unitMatch[2];
    if (isNaN(num)) return 0;
    if (unit === "k") return Math.round(num * 1000);
    if (unit === "m") return Math.round(num * 1000000);
  }

  const num = parseFloat(s.replace(/[^\d.]/g, ""));
  if (isNaN(num)) return 0;
  return Math.round(num);
}

/* ----- arrangement fee formatting for table cell ----- */
function formatArrangementFeeCell(p) {
  // Try to get fee value from mapped field or raw doc
  const rawFee =
    p.arrangementFee ??
    p._raw?.product_fee_amount ??
    p._raw?.arrangement_fee_percent ??
    "";

  const feeStr = String(rawFee ?? "").trim();
  if (!feeStr) return "—";

  // Determine fee type (Pound / Percentage / Mini Fee)
  const feeType =
    p.feeType ||
    p.productFeeType ||
    p.product_fee_type ||
    p._raw?.product_fee_type ||
    "";

  const typeLower = String(feeType).toLowerCase();
  const numeric = feeStr.replace(/[^\d.,]/g, "");

  if (typeLower === "percentage") {
    return numeric ? `${numeric}%` : `${feeStr}%`;
  }

  // Mini Fee -> "Min £999"
  if (typeLower === "mini fee") {
    return numeric ? `Min £${numeric}` : `Min £${feeStr}`;
  }

  // Pound (or anything else default) -> "£999"
  return numeric ? `£${numeric}` : `£${feeStr}`;
}

export default function ListedProducts() {
  const location = useLocation();
  const navigate = useNavigate();

  const lineFromUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = String(params.get("product") || params.get("line") || "").trim().toLowerCase();
    if (raw === "if" || raw === "invoice" || raw === "invoice-finance") return "if";
    if (raw === "btl" || raw === "buy-to-let") return "btl";
    if (raw === "cbtl" || raw === "complex-btl" || raw === "complex") return "cbtl";
    return null;
  }, [location.search]);

  const [productLine, setProductLine] = React.useState(() => lineFromUrl || "btl");
  const isMortgageLine = productLine !== "if";
  const mortgageCategory = MORTGAGE_LINE_CATEGORY[productLine] || "BTL";
  const [lineReady, setLineReady] = React.useState(() => !!lineFromUrl);
  const urlLineAppliedRef = React.useRef(false);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [q, setQ] = React.useState("");
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [categoryFilter, setCategoryFilter] = React.useState("ALL");
  const [sectorFilter, setSectorFilter] = React.useState("ALL");
  const [sectorOptions, setSectorOptions] = React.useState([]);
  const [status, setStatus] = React.useState("ALL");
  const [sortBy, setSortBy] = React.useState("DEFAULT");
  const [minTerm, setMinTerm] = React.useState("");
  const [maxTerm, setMaxTerm] = React.useState("");
  const [minArr, setMinArr] = React.useState("");
  const [maxArr, setMaxArr] = React.useState("");

  const [viewing, setViewing] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [pendingStatusChange, setPendingStatusChange] = React.useState(null);
  const [updateSuccessMessage, setUpdateSuccessMessage] = React.useState("");
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showStatusDialog, setShowStatusDialog] = React.useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);

  // Multiple selection state
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const selectionAnchorIdRef = React.useRef(null);

  const pageSize = 10;
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    if (!pendingDelete) {
      setShowDeleteDialog(false);
      return;
    }
    const id = requestAnimationFrame(() => setShowDeleteDialog(true));
    return () => cancelAnimationFrame(id);
  }, [pendingDelete]);

  React.useEffect(() => {
    if (!pendingStatusChange) {
      setShowStatusDialog(false);
      return;
    }
    const id = requestAnimationFrame(() => setShowStatusDialog(true));
    return () => cancelAnimationFrame(id);
  }, [pendingStatusChange]);

  React.useEffect(() => {
    if (!updateSuccessMessage) {
      setShowSuccessDialog(false);
      return;
    }
    const id = requestAnimationFrame(() => setShowSuccessDialog(true));
    return () => cancelAnimationFrame(id);
  }, [updateSuccessMessage]);

  React.useEffect(() => {
    // Apply ?line= / ?product= only once — don't override later manual dropdown changes.
    if (lineFromUrl && !urlLineAppliedRef.current) {
      urlLineAppliedRef.current = true;
      setProductLine(lineFromUrl);
      setLineReady(true);
      return;
    }
    if (lineFromUrl || lineReady) return;

    let cancelled = false;
    (async () => {
      try {
        const [btlRows, ifRows] = await Promise.all([
          getMortgageProducts({
            product_category: "BTL",
            limit_page_length: 1,
          }).catch(() => []),
          getInvoiceFinanceProducts({
            limit_page_length: 1,
          }).catch(() => []),
        ]);

        const hasBtl = (Array.isArray(btlRows) ? btlRows : []).some(
          (p) => !isInvoiceFinanceProduct(p)
        );
        const hasIf = (Array.isArray(ifRows) ? ifRows : []).length > 0;

        // Prefer the first product line that actually has listed products.
        // Order follows HERO_PRODUCT_OPTIONS (BTL, then Invoice Finance).
        let next = "btl";
        if (hasBtl) next = "btl";
        else if (hasIf) next = "if";

        if (!cancelled) setProductLine(next);
      } catch {
        if (!cancelled) setProductLine("btl");
      } finally {
        if (!cancelled) setLineReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lineFromUrl, lineReady]);

  React.useEffect(() => {
    if (!lineReady) {
      setLoading(true);
      return;
    }
    loadFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLine, lineReady]);

  // Backend product ID pattern (e.g. BTL-003683)
  const looksLikeProductId = (s) => /^[A-Za-z]{2,5}-\d{3,}$/.test(String(s || "").trim());

  async function loadFromBackend(extraParams = {}) {
    if (!lineReady) return;
    setLoading(true);
    setError("");
    try {
      const shared = {
        ...(q ? { product_name__like: q } : {}),
        ...(q && looksLikeProductId(q) ? { name: q.trim() } : {}),
        ...(status !== "ALL" ? { status } : {}),
        ...extraParams,
        limit_page_length: 1000,
      };

      if (productLine === "if") {
        const params = {
          ...shared,
          ...(sectorFilter !== "ALL" ? { sector: sectorFilter } : {}),
        };
        const rows = await getInvoiceFinanceProducts(params);
        setItems(rows);
        const sectorsFromRows = [
          ...new Set(
            rows
              .map((p) => String(p.sector || p.product || "").trim())
              .filter((s) => s && s !== "—")
          ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        // Keep the full sector list when a sector filter is applied.
        if (sectorFilter === "ALL") {
          setSectorOptions(sectorsFromRows);
        } else {
          setSectorOptions((prev) => {
            const merged = new Set([...(prev || []), ...sectorsFromRows]);
            if (sectorFilter) merged.add(sectorFilter);
            return [...merged].sort((a, b) =>
              a.localeCompare(b, undefined, { sensitivity: "base" })
            );
          });
        }
      } else {
        const params = {
          ...shared,
          product_category: mortgageCategory,
          category: mortgageCategory,
          ...(categoryFilter !== "ALL" ? { product_type: categoryFilter } : {}),
        };
        const wantCbtl = productLine === "cbtl";
        const keep = (list) =>
          list.filter((p) => !isInvoiceFinanceProduct(p) && isCbtlRow(p) === wantCbtl);

        let rows = keep(await getMortgageProducts(params));
        if (wantCbtl && rows.length === 0) {
          // Backend may not filter by CBTL yet — fetch without a category and pick CBTL rows.
          const { product_category, category, ...withoutCategory } = params;
          rows = keep(await getMortgageProducts(withoutCategory));
        }
        setItems(rows);
        setSectorOptions([]);
      }
      setPage(1);
    } catch (e) {
      setError(e.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  }

  // handle ?q=Product+Name in URL
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qFromUrl = params.get("q") || "";
    if (qFromUrl) {
      setQ(qFromUrl);
      setPage(1);
      const extra = { product_name__like: qFromUrl };
      if (looksLikeProductId(qFromUrl)) extra.name = qFromUrl.trim();
      loadFromBackend(extra);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  function resetFilters() {
    setQ("");
    setCategoryFilter("ALL");
    setSectorFilter("ALL");
    setStatus("ALL");
    setSortBy("DEFAULT");
    setMinTerm("");
    setMaxTerm("");
    setMinArr("");
    setMaxArr("");
    setPage(1);
  }

  function handleProductLineChange(line) {
    setProductLine(line);
    setPage(1);
    setSelectedIds(new Set());
    setSectorOptions([]);
    resetFilters();

    // Keep URL in sync so ?line=if doesn't snap the dropdown back to IF.
    const params = new URLSearchParams(location.search);
    params.set("line", line);
    params.delete("product");
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    urlLineAppliedRef.current = true;
  }

  // Drop expiry sort when switching to IF (no expiry dates on IF products).
  React.useEffect(() => {
    if (productLine === "if" && sortBy === "EXPIRY_ASC") {
      setSortBy("DEFAULT");
    }
  }, [productLine, sortBy]);

  // client-side filters
  const filtered = React.useMemo(
    () =>
      items.filter((p) => {
        if (productLine === "if" && !isInvoiceFinanceProduct(p)) return false;
        if (isMortgageLine && isInvoiceFinanceProduct(p)) return false;

        const cat =
          p.category || p._raw?.product_category || p._raw?.category || "";
        const ifCat = p.ifCategory || p.type || p._raw?.product_type || "";
        const btlType =
          p.type || p._raw?.product_type || p.ifCategory || "—";

        const hit =
          !q ||
          norm(p.product).includes(norm(q)) ||
          norm(p.sector).includes(norm(q)) ||
          norm(p._id || "").includes(norm(q)) ||
          norm(cat).includes(norm(q)) ||
          norm(btlType).includes(norm(q)) ||
          norm(ifCat).includes(norm(q)) ||
          norm(p.contractTerm || p.term).includes(norm(q)) ||
          norm(p.status).includes(norm(q)) ||
          norm(p.range).includes(norm(q)) ||
          norm(p.serviceFee).includes(norm(q)) ||
          norm(p.discountRate).includes(norm(q)) ||
          norm(p.ip).includes(norm(q));
        if (!hit) return false;

        if (isMortgageLine) {
          if (categoryFilter !== "ALL" && norm(btlType) !== norm(categoryFilter))
            return false;
        }

        if (productLine === "if" && sectorFilter !== "ALL") {
          const sectorLabel = String(p.sector || p.product || "").trim();
          if (norm(sectorLabel) !== norm(sectorFilter)) return false;
        }

        if (status !== "ALL" && normalizeStatus(p.status) !== normalizeStatus(status)) return false;

        const tMin = minTerm === "" ? -Infinity : parseFloat(minTerm);
        const tMax = maxTerm === "" ? +Infinity : parseFloat(maxTerm);
        const termVal =
          productLine === "if" ? p._termMonths : p._termYears;
        if (termVal != null && !(termVal >= tMin && termVal <= tMax)) return false;

        const aMin = minArr === "" ? -Infinity : parseFloat(minArr);
        const aMax = maxArr === "" ? +Infinity : parseFloat(maxArr);
        if (isMortgageLine) {
          if (
            p._arrangePct != null &&
            !(p._arrangePct >= aMin && p._arrangePct <= aMax)
          )
            return false;
        } else if (
          p._discountRateNum != null &&
          !(p._discountRateNum >= aMin && p._discountRateNum <= aMax)
        )
          return false;

        return true;
      }),
    [items, q, categoryFilter, sectorFilter, status, minTerm, maxTerm, minArr, maxArr, productLine, isMortgageLine]
  );

  const sortedForDisplay = React.useMemo(() => {
    const list = [...filtered];
    if (sortBy === "LENDER_CODE_ASC") {
      return list.sort((a, b) => {
        const aCode = String(
          a.lender_product_code ||
            a._raw?.lender_product_code ||
            a._raw?.lenderProductCode ||
            a._id ||
            ""
        ).trim();
        const bCode = String(
          b.lender_product_code ||
            b._raw?.lender_product_code ||
            b._raw?.lenderProductCode ||
            b._id ||
            ""
        ).trim();
        return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: "base" });
      });
    }
    // Expiry sorting is BTL-only — IF products have no expiry dates.
    if (sortBy === "EXPIRY_ASC" && isMortgageLine) {
      return list.sort((a, b) => {
        const da = toDateSafe(getProductExpiryRaw(a));
        const db = toDateSafe(getProductExpiryRaw(b));
        if (da && db) return da.getTime() - db.getTime();
        if (da && !db) return -1;
        if (!da && db) return 1;
        return 0;
      });
    }
    if (productLine === "if") {
      return list.sort((a, b) => {
        const aSector = String(a.sector || a.product || "").trim();
        const bSector = String(b.sector || b.product || "").trim();
        const bySector = aSector.localeCompare(bSector, undefined, {
          sensitivity: "base",
        });
        if (bySector !== 0) return bySector;
        return String(a._id || "").localeCompare(String(b._id || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }
    return list.sort((a, b) => {
      const aUrgent = isExpiringWithin7Days(getProductExpiryRaw(a));
      const bUrgent = isExpiringWithin7Days(getProductExpiryRaw(b));
      if (aUrgent !== bUrgent) return aUrgent ? -1 : 1;
      return 0;
    });
  }, [filtered, sortBy, productLine, isMortgageLine]);

  const totalPages = Math.max(1, Math.ceil(sortedForDisplay.length / pageSize));
  const pageData = React.useMemo(
    () => sortedForDisplay.slice((page - 1) * pageSize, page * pageSize),
    [sortedForDisplay, page]
  );

  async function deleteOne(id) {
    setPendingDelete({ mode: "single", id });
  }

  const deleteProductById = (idOrRow) =>
    productLine === "if"
      ? deleteInvoiceFinanceProduct(idOrRow)
      : deleteMortgageProduct(idOrRow);

  const updateProductStatusById = (idOrRow, status) =>
    productLine === "if"
      ? updateInvoiceFinanceProduct(idOrRow, { status })
      : updateMortgageProduct(idOrRow, { status });

  async function openViewProduct(item) {
    if (productLine === "if" && item?._id) {
      try {
        const full = await getInvoiceFinanceProduct(item._id);
        setViewing(full);
      } catch (e) {
        alert(e.message || "Failed to load product details");
      }
      return;
    }
    setViewing(item);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    if (pendingDelete.mode === "single") {
      const id = pendingDelete.id;
      try {
        await deleteProductById(id);
        setItems((prev) => prev.filter((p) => p._id !== id));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setUpdateSuccessMessage("Product deleted successfully.");
      } catch (e) {
        alert(e.message || "Delete failed");
      } finally {
        setPendingDelete(null);
      }
      return;
    }

    const idsToDelete = pendingDelete.ids || [];
    const count = idsToDelete.length;
    const failed = [];

    try {
      // Delete all selected products
      await Promise.allSettled(
        idsToDelete.map(async (id) => {
          try {
            await deleteProductById(id);
          } catch (e) {
            failed.push({ id, error: e.message || "Delete failed" });
          }
        })
      );

      // Update items list
      const idsSet = new Set(idsToDelete);
      setItems((prev) => prev.filter((p) => !idsSet.has(p._id)));

      // Clear selection
      setSelectedIds(new Set());

      if (failed.length > 0) {
        setUpdateSuccessMessage(
          `Deleted ${count - failed.length} product(s). ${failed.length} failed.`
        );
      } else {
        setUpdateSuccessMessage(`Successfully deleted ${count} product(s).`);
      }
    } catch (e) {
      alert(e.message || "Bulk delete failed");
    } finally {
      setPendingDelete(null);
    }
  }

  // Bulk delete function
  async function deleteMultiple() {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setPendingDelete({ mode: "multiple", ids });
  }

  async function deactivateMultiple() {
    if (selectedIds.size === 0) return;
    setPendingStatusChange({
      action: "deactivate",
      targetStatus: "Deactivated",
      ids: Array.from(selectedIds),
    });
  }

  async function activateMultiple() {
    if (selectedIds.size === 0) return;
    setPendingStatusChange({
      action: "activate",
      targetStatus: "Active",
      ids: Array.from(selectedIds),
    });
  }

  async function confirmStatusChange() {
    if (!pendingStatusChange) return;
    const ids = pendingStatusChange.ids || [];
    const failed = [];

    try {
      await Promise.allSettled(
        ids.map(async (id) => {
          try {
            await updateProductStatusById(id, pendingStatusChange.targetStatus);
          } catch (e) {
            failed.push({ id, error: e.message || `${pendingStatusChange.action} failed` });
          }
        })
      );

      // Refresh from backend to keep status/pagination synchronized
      await loadFromBackend();
      setSelectedIds(new Set());

      if (failed.length > 0) {
        const failedIds = failed.map((f) => f.id).join(", ");
        setUpdateSuccessMessage(
          `${pendingStatusChange.action === "activate" ? "Activated" : "Deactivated"} ${ids.length - failed.length} product(s). ${failed.length} failed: ${failedIds}.`
        );
      } else {
        setUpdateSuccessMessage(
          `Successfully ${pendingStatusChange.action === "activate" ? "activated" : "deactivated"} ${ids.length} product(s).`
        );
      }
    } catch (e) {
      alert(e.message || `Bulk ${pendingStatusChange.action} failed`);
    } finally {
      setPendingStatusChange(null);
    }
  }

  // Toggle one product, or Shift+click to select the range from the last anchor.
  function toggleSelection(id, event) {
    const orderedIds = sortedForDisplay.map((p) => p._id).filter(Boolean);
    setSelectedIds((prev) => {
      const { selected, anchorId } = applyListSelection(prev, {
        id,
        orderedIds,
        shiftKey: Boolean(event?.shiftKey),
        anchorId: selectionAnchorIdRef.current,
      });
      selectionAnchorIdRef.current = anchorId;
      return selected;
    });
  }

  // Toggle select all across every filtered product (all pages)
  function toggleSelectAll() {
    const allIds = sortedForDisplay.map((p) => p._id).filter(Boolean);
    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  // Header checkbox — true when every filtered product is selected
  const allPageSelected = React.useMemo(() => {
    if (sortedForDisplay.length === 0) return false;
    return sortedForDisplay.every((p) => selectedIds.has(p._id));
  }, [sortedForDisplay, selectedIds]);

  const selectedItems = React.useMemo(
    () => items.filter((p) => selectedIds.has(p._id)),
    [items, selectedIds]
  );
  const selectedStatusKinds = React.useMemo(
    () => new Set(selectedItems.map((p) => normalizeStatus(p.status))),
    [selectedItems]
  );
  const canBulkDeactivate =
    selectedItems.length > 0 &&
    selectedStatusKinds.size === 1 &&
    selectedStatusKinds.has("active");
  const canBulkActivate =
    selectedItems.length > 0 &&
    selectedStatusKinds.size === 1 &&
    selectedStatusKinds.has("deactivated");

  async function saveEditedIf(nextItem) {
    try {
      const [minRaw, maxRaw] = (nextItem.range || "")
        .split("-")
        .map((x) => x.trim());
      const minLoan = parseLoanValue(minRaw || "") || nextItem._minLoan;
      const maxLoan = parseLoanValue(maxRaw || "") || nextItem._maxLoan;
      const parsePct = (v) => {
        const n = parseFloat(String(v || "").replace(/[^\d.]/g, ""));
        return isNaN(n) ? null : n;
      };
      const parseMoneyField = (v) => {
        if (v == null || v === "" || v === "—") return null;
        const parsed = parseLoanValue(String(v));
        return parsed || null;
      };
      const serviceFeeToApi = (displayVal) => {
        const n = parsePct(displayVal);
        return n == null ? null : n;
      };

      const normStatus = normalizeStatus(nextItem.status);
      const termMonths =
        parseInt(String(nextItem.term || "").replace(/[^\d]/g, ""), 10) ||
        nextItem._termMonths ||
        null;

      const payload = {
        sector: nextItem.sector,
        status: normStatus === "deactivated" ? "Deactivated" : "Active",
        min_limit: minLoan,
        max_limit: maxLoan,
        discount_rate_percent: parsePct(nextItem.discountRate),
        ip_percent: parsePct(nextItem.ip),
        service_fee_percent: serviceFeeToApi(nextItem.serviceFee),
        min_service_fee:
          parseMoneyField(nextItem.miniServiceFee) ??
          nextItem._raw?.min_service_fee ??
          null,
        contract_term_months: termMonths,
      };

      await updateInvoiceFinanceProduct(nextItem, payload);
      await loadFromBackend();
      setEditing(null);
      setUpdateSuccessMessage("Product updated successfully.");
    } catch (e) {
      alert(e.message || "Update failed");
    }
  }

  async function saveEdited(nextItem) {
    try {
      // ----- amount range -----
      const [minRaw, maxRaw] = (nextItem.range || "")
        .split("-")
        .map((x) => x.trim());

      const minLoan = parseLoanValue(minRaw || "");
      const maxLoan = parseLoanValue(maxRaw || "");

      // ----- arrangement fee numeric value -----
      const feeStr = String(nextItem.arrangementFee || "").trim();
      const feeNumeric = parseFloat(
        feeStr.replace(",", "").replace(/[^\d.]/g, "")
      );

      // ----- fee type: prefer explicit from modal, else infer from text -----
      let feeType =
        nextItem.feeType ||
        nextItem.productFeeType ||
        nextItem.product_fee_type ||
        nextItem._raw?.product_fee_type ||
        null;

      const low = feeStr.toLowerCase();
      if (!feeType) {
        // Frappe expects: "Pound", "Percentage", "Mini Fee"
        if (low.includes("%")) {
          feeType = "Percentage";
        } else if (low.includes("mini") || low.includes("min")) {
          feeType = "Mini Fee";
        } else {
          feeType = "Pound";
        }
      }

      const normStatus = normalizeStatus(nextItem.status);
      let frappeDateTime = null;
      if (normStatus === "scheduled") {
        frappeDateTime = scheduleDatetimeLocalToFrappe(
          String(nextItem.scheduleAt || "").trim()
        );
        if (!frappeDateTime) {
          alert("Please select a go-live date and time for the scheduled product.");
          return;
        }
      }

      const payload = {
        product_name: nextItem.product,
        product_category:
          nextItem.category ||
          nextItem._raw?.product_category ||
          "BTL",
        // Use canonical Mortgage Product field names expected by Frappe.
        product_rate_percent: parseFloat(
          String(nextItem.rate || "").replace(/[^\d.]/g, "")
        ),
        mortgage_term_years: parseFloat(
          String(nextItem.term || "").replace(/[^\d.]/g, "")
        ),
        product_fee_amount: isNaN(feeNumeric) ? null : feeNumeric,
        min_loan: minLoan,
        max_loan: maxLoan,
        product_expiray_date: nextItem.expiryDate || null,
        // DocType only allows Active | Deactivated; scheduling uses product_schedule + date_time.
        status:
          normStatus === "scheduled"
            ? "Active"
            : normStatus === "deactivated"
              ? "Deactivated"
              : "Active",
      };

      if (feeType) payload.product_fee_type = feeType;

      if (normStatus === "scheduled") {
        payload.product_schedule = 1;
        payload.date_time = frappeDateTime;
      } else {
        payload.product_schedule = 0;
        payload.date_time = null;
      }

      await updateMortgageProduct(nextItem._id, payload);

      const existing = items.find((p) => p._id === nextItem._id) || {};
      const prevExpiry =
        String(
          existing?.expiryDate ||
            existing?.expiry_date ||
            existing?.product_expiry_date ||
            existing?.product_expiray_date ||
            existing?._raw?.expiry_date ||
            existing?._raw?.product_expiry_date ||
            existing?._raw?.product_expiray_date ||
            ""
        ).trim();
      const nextExpiry = String(nextItem.expiryDate || "").trim();
      const expiryChanged = prevExpiry !== nextExpiry;

      await loadFromBackend();

      setEditing(null);
      const statusMessage =
        expiryChanged && normStatus !== "scheduled"
          ? "Product expiry date updated successfully."
          : normStatus === "scheduled"
          ? "Product scheduled successfully."
          : normStatus === "active"
            ? "Product activated successfully."
            : normStatus === "deactivated"
              ? "Product deactivated successfully."
              : "Product updated successfully.";
      setUpdateSuccessMessage(statusMessage);
    } catch (e) {
      alert(e.message || "Update failed");
    }
  }

  // skeleton loader
  const skeletonRows = Array.from({ length: 5 }, (_, i) => (
    <tr
      key={`skeleton-${i}`}
      className="border-t border-gray-200 animate-pulse"
    >
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-4 w-40 bg-sky-100 rounded" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-6 w-16 bg-sky-50 rounded-full" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-4 w-16 bg-sky-100 rounded" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-4 w-24 bg-sky-100 rounded" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-4 w-16 bg-sky-100 rounded" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-4 w-28 bg-sky-100 rounded" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="h-6 w-20 bg-sky-50 rounded-full" />
      </td>
      <td className="px-2 py-2.5 sm:px-3 sm:py-3">
        <div className="flex items-center justify-center gap-2">
          <div className="h-5 w-5 bg-sky-50 rounded-full" />
          <div className="h-5 w-5 bg-sky-50 rounded-full" />
          <div className="h-5 w-5 bg-sky-50 rounded-full" />
        </div>
      </td>
    </tr>
  ));

  return (
    <>
      <Hero
        title="Listed Products"
        subtitle={
          productLine === "if"
            ? "Manage your invoice finance listed products."
            : "View all your listed products here."
        }
        right={
          <label className="flex w-full flex-col gap-1.5 sm:w-auto sm:flex-row sm:items-center sm:gap-2 md:shrink-0">
            <span className="text-sm/none font-medium opacity-90">Product:</span>
            <select
              value={productLine}
              onChange={(e) => handleProductLineChange(e.target.value)}
              aria-label="Product type"
              className="w-full max-w-full text-slate-800 bg-white rounded-md px-3 py-2.5 border border-sky-200 shadow-sm focus:outline-none sm:w-auto sm:min-w-[12rem]"
            >
              {HERO_PRODUCT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        }
      />

      <Card className="mt-4 max-w-full min-w-0 overflow-visible p-3 sm:p-5">
        {/* Search + buttons */}
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/95 p-3 shadow-sm sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="relative min-h-[48px] w-full min-w-0 flex-1 sm:min-h-[44px] sm:min-w-[200px]">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder={
                productLine === "if"
                  ? "Search sector, category, fees, term, range…"
                  : "Search products (name, category, status, term, range)…"
              }
              className="h-12 w-full rounded-xl border-2 border-slate-300 bg-white px-3 py-2.5 pr-10 text-base text-slate-900 shadow-sm placeholder:text-slate-500 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-11 sm:border sm:text-sm"
            />
          </div>

          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="h-12 w-full min-w-0 appearance-none rounded-xl border-2 border-slate-300 bg-white px-4 py-2 pl-4 pr-11 text-base font-semibold text-slate-900 shadow-sm hover:bg-slate-50 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-11 sm:w-auto sm:min-w-[16rem] sm:max-w-[min(100%,26rem)] sm:border sm:text-sm md:min-w-[22rem]"
            style={{
              backgroundImage: SORT_SELECT_CHEVRON_BG,
              backgroundPosition: "right 1.15rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1rem 1rem",
            }}
            title="Sort products"
          >
            <option value="DEFAULT">
              {productLine === "if" ? "Sort: Default (Sector A-Z)" : "Sort: Default"}
            </option>
            {isMortgageLine && (
              <option value="EXPIRY_ASC">Sort: Expiry (earliest first)</option>
            )}
            <option value="LENDER_CODE_ASC">Sort: Lender Product Code (A-Z)</option>
          </select>

          {selectedIds.size > 0 && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
              {canBulkActivate && (
                <button
                  type="button"
                  onClick={activateMultiple}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-base font-semibold text-white shadow-md hover:bg-emerald-700 active:bg-emerald-800 sm:w-auto sm:py-2 sm:text-sm"
                >
                  Activate Selected ({selectedIds.size})
                </button>
              )}
              {canBulkDeactivate && (
                <button
                  type="button"
                  onClick={deactivateMultiple}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-3 text-base font-semibold text-white shadow-md hover:bg-amber-700 active:bg-amber-800 sm:w-auto sm:py-2 sm:text-sm"
                >
                  Deactivate Selected ({selectedIds.size})
                </button>
              )}
              <button
                type="button"
                onClick={deleteMultiple}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-3 text-base font-semibold text-white shadow-md hover:bg-rose-700 active:bg-rose-800 sm:w-auto sm:py-2 sm:text-sm"
              >
                <Trash2 className="h-4 w-4" /> Delete Selected ({selectedIds.size})
              </button>
            </div>
          )}

          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:gap-2">
            <button
              type="button"
              onClick={() => loadFromBackend()}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 text-base font-semibold text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 sm:h-11 sm:w-auto sm:border sm:py-2 sm:text-sm"
            >
              <RefreshCw className="h-5 w-5 shrink-0" /> Refresh
            </button>

            <div className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                aria-expanded={filtersOpen}
                aria-controls="listed-products-filters-panel"
                className="h-12 w-full rounded-xl border-2 border-slate-300 bg-white px-4 text-base font-semibold text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 sm:h-11 sm:w-auto sm:border sm:py-2 sm:text-sm"
              >
                Filters
              </button>
              {filtersOpen && (
                <div
                  id="listed-products-filters-panel"
                  className="absolute left-0 right-0 z-[100] mt-2 w-full max-w-[min(100vw-2rem,720px)] sm:left-auto sm:right-0 sm:w-[min(92vw,720px)]"
                >
                  <div className="max-h-[min(75vh,560px)] overflow-y-auto overscroll-contain rounded-2xl border-2 border-slate-200 bg-white p-4 shadow-2xl ring-1 ring-black/5">
                    <div className="mb-3 flex items-center justify-between gap-2 border-b border-slate-200 pb-3">
                      <span className="text-sm font-bold text-slate-900">Filters</span>
                      <button
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        aria-label="Close filters"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* TYPE / SECTOR FILTER */}
                    <label className="block">
                      <div className="text-xs font-semibold text-gray-600 mb-1">
                        {productLine === "if" ? "Sector" : "Type"}
                      </div>
                      {productLine === "if" ? (
                        <select
                          value={sectorFilter}
                          onChange={(e) => {
                            setSectorFilter(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        >
                          <option value="ALL">ALL</option>
                          {sectorOptions.map((sector) => (
                            <option key={sector} value={sector}>
                              {sector}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={categoryFilter}
                          onChange={(e) => {
                            setCategoryFilter(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        >
                          <option>ALL</option>
                          <option>Fixed</option>
                          <option>Variable</option>
                          <option>Tracker</option>
                          <option>Discounted</option>
                        </select>
                      )}
                    </label>

                    <label className="block">
                      <div className="text-xs font-semibold text-gray-600 mb-1">
                        Status
                      </div>
                      <select
                        value={status}
                        onChange={(e) => {
                          setStatus(e.target.value);
                          setPage(1);
                        }}
                        className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                      >
                        <option>ALL</option>
                        <option>Active</option>
                        <option>Deactivated</option>
                        <option>Scheduled</option>
                      </select>
                    </label>

                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <div className="text-xs font-semibold text-gray-600 mb-1">
                          {productLine === "if" ? "Contract Term (mo) Min" : "Term (yrs) Min"}
                        </div>
                        <input
                          type="number"
                          value={minTerm}
                          onChange={(e) => {
                            setMinTerm(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        />
                      </label>

                      <label className="block">
                        <div className="text-xs font-semibold text-gray-600 mb-1">
                          {productLine === "if" ? "Contract Term (mo) Max" : "Term (yrs) Max"}
                        </div>
                        <input
                          type="number"
                          value={maxTerm}
                          onChange={(e) => {
                            setMaxTerm(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <div className="text-xs font-semibold text-gray-600 mb-1">
                          {productLine === "if" ? "Discount rate % Min" : "Arr. Fee % Min"}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={minArr}
                          onChange={(e) => {
                            setMinArr(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        />
                      </label>

                      <label className="block">
                        <div className="text-xs font-semibold text-gray-600 mb-1">
                          {productLine === "if" ? "Discount rate % Max" : "Arr. Fee % Max"}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={maxArr}
                          onChange={(e) => {
                            setMaxArr(e.target.value);
                            setPage(1);
                          }}
                          className="h-12 w-full rounded-lg border-2 border-slate-300 bg-white px-3 text-base text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:h-10 sm:text-sm"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="w-full rounded-lg border-2 border-slate-300 bg-white px-4 py-3 text-base font-semibold text-slate-900 shadow-sm hover:bg-slate-50 sm:w-auto sm:py-2 sm:text-sm"
                    >
                      Reset
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const params = {
                          ...(q ? { product_name__like: q } : {}),
                          ...(productLine === "if" && sectorFilter !== "ALL"
                            ? { sector: sectorFilter }
                            : {}),
                          ...(isMortgageLine
                            ? {
                                product_category: mortgageCategory,
                                category: mortgageCategory,
                                ...(categoryFilter !== "ALL"
                                  ? { product_type: categoryFilter }
                                  : {}),
                              }
                            : {}),
                          ...(status !== "ALL" ? { status } : {}),
                        };
                        loadFromBackend(params);
                        setFiltersOpen(false);
                      }}
                      className="w-full rounded-lg bg-sky-600 px-4 py-3 text-base font-semibold text-white shadow-md hover:bg-sky-700 active:bg-sky-800 sm:w-auto sm:py-2 sm:text-sm"
                    >
                      Apply &amp; Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-800">
            Error: {error}
          </div>
        )}

        <div className="w-full max-w-full min-w-0 -mx-1 touch-pan-x overflow-x-auto px-1 sm:mx-0 sm:px-0">
          <table
            className={`w-full max-w-full text-xs sm:text-sm ${
              productLine === "if"
                ? "min-w-[720px] table-fixed"
                : "min-w-[720px] table-auto sm:table-fixed"
            }`}
          >
            {productLine === "if" ? (
              <colgroup>
                <col className="w-[44px]" />
                <col className="w-[16%]" />
                <col className="w-[7%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[14%]" />
                <col className="w-[10%]" />
                <col className="w-[12%]" />
              </colgroup>
            ) : (
              <colgroup>
                <col className="w-[5%]" />
                <col className="w-[13%]" />
                <col className="w-[12%]" />
                <col className="w-[10%]" />
                <col className="w-[12%]" />
                <col className="w-[10%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
              </colgroup>
            )}

            <thead>
              <tr className="bg-gray-50 text-gray-600">
                <th className="w-10 px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-slate-100 sm:min-h-0 sm:min-w-0 sm:p-0"
                    title={allPageSelected ? "Deselect all" : "Select all"}
                  >
                    {allPageSelected ? (
                      <CheckSquare className="h-5 w-5 text-sky-600" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </th>
                {productLine === "if" ? (
                  <>
                    <th className="px-2 py-2.5 text-left font-semibold sm:px-3 sm:py-3">SECTOR</th>
                    <th className="px-1.5 py-2.5 text-center font-semibold sm:px-2 sm:py-3" title="Initial Payment">
                      IP
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold leading-tight sm:px-2 sm:py-3" title="Service Fee">
                      <span className="block whitespace-nowrap">SERVICE</span>
                      <span className="block whitespace-nowrap">FEE</span>
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold leading-tight sm:px-2 sm:py-3" title="Mini Service Fee">
                      <span className="block whitespace-nowrap">MINI</span>
                      <span className="block whitespace-nowrap">SERVICE FEE</span>
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold leading-tight sm:px-2 sm:py-3" title="Discount Rate">
                      <span className="block whitespace-nowrap">DISC.</span>
                      <span className="block whitespace-nowrap">RATE</span>
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold leading-tight sm:px-2 sm:py-3" title="Contract Term">
                      <span className="block whitespace-nowrap">TERM</span>
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold sm:px-2 sm:py-3" title="Amount Range">
                      RANGE
                    </th>
                    <th className="px-1.5 py-2.5 text-center font-semibold sm:px-2 sm:py-3">STATUS</th>
                    <th className="px-1.5 py-2.5 text-center font-semibold sm:px-2 sm:py-3">ACTIONS</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2.5 text-left font-semibold sm:px-3 sm:py-3">PRODUCT</th>
                    <th className="px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">TYPE</th>
                    <th className="px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">RATE</th>
                    <th className="whitespace-nowrap px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">
                      ARRANGEMENT FEE
                    </th>
                    <th className="px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">TERM</th>
                    <th className="whitespace-nowrap px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">
                      AMOUNT RANGE
                    </th>
                    <th className="px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">STATUS</th>
                    <th className="px-2 py-2.5 text-center font-semibold sm:px-3 sm:py-3">ACTIONS</th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                skeletonRows
              ) : (
                <>
                  {pageData.map((p) => {
                    const btlProductType =
                      p.type || p._raw?.product_type || "—";
                    const lenderProductCode =
                      p.lender_product_code ||
                      p._raw?.lender_product_code ||
                      p._raw?.lenderProductCode ||
                      (productLine === "if" ? p._id : null) ||
                      "—";
                    const expiryRaw = getProductExpiryRaw(p);
                    const showExpiryWarning =
                      isMortgageLine && isExpiringWithin7Days(expiryRaw);
                    const isSelected = selectedIds.has(p._id);
                    const sectorLabel = p.sector || p.product || "—";

                    return (
                      <tr
                        key={p._id}
                        className={`border-t border-gray-200 hover:bg-gray-50 ${
                          isSelected ? "bg-sky-50" : ""
                        }`}
                      >
                        <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              if (e.shiftKey) e.preventDefault();
                            }}
                            onClick={(e) => toggleSelection(p._id, e)}
                            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-slate-100 sm:min-h-0 sm:min-w-0"
                            title={isSelected ? "Deselect" : "Select"}
                          >
                            {isSelected ? (
                              <CheckSquare className="h-5 w-5 text-sky-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                        {productLine === "if" ? (
                          <>
                            <td
                              className="max-w-0 px-2 py-2 sm:px-3 sm:py-2.5 font-medium text-gray-900"
                              title={sectorLabel}
                            >
                              <div className="min-w-0 truncate">
                                <SectorBadge sector={sectorLabel} compact />
                              </div>
                              <div className="mt-0.5 truncate text-[10px] text-sky-600 sm:text-xs">
                                {lenderProductCode}
                              </div>
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              {p.ip}
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              {p.serviceFee}
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              {p.miniServiceFee}
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              {p.discountRate}
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              {p.contractTerm ?? p.term}
                            </td>
                            <td className="px-1.5 py-2 text-center tabular-nums sm:px-2 sm:py-2.5">
                              <span className="block truncate" title={p.range}>
                                {p.range}
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td
                              className="px-2 py-2.5 sm:px-3 sm:py-3 font-medium text-gray-900 truncate"
                              title={p.product}
                            >
                              {showExpiryWarning && (
                                <div className="text-xs text-rose-600 font-semibold mb-0.5 truncate">
                                  Expires: {String(expiryRaw).trim()}
                                </div>
                              )}
                              <div className="truncate">{p.product}</div>
                              <div className="text-xs text-sky-600 mt-0.5">
                                {lenderProductCode}
                              </div>
                            </td>
                            <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">
                              <MortgageProductTypeBadge type={btlProductType} />
                            </td>
                            <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">{p.rate}</td>
                            <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center tabular-nums">
                              {formatArrangementFeeCell(p)}
                            </td>
                            <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">{p.term}</td>
                            <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center whitespace-nowrap">
                              {p.range}
                            </td>
                          </>
                        )}
                        <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <CenteredStatusPill status={p.status} />
                            {normalizeStatus(p.status) === "scheduled" && (
                              <span className="text-[11px] text-amber-700 font-medium">
                                {formatScheduledDateTime(p.date_time || p._raw?.date_time) || "Date/time pending"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 sm:px-3 sm:py-3">
                          <div className="flex items-center justify-center gap-2 text-gray-600 sm:gap-3">
                            <button
                              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-slate-100 hover:text-sky-700 sm:min-h-0 sm:min-w-0 sm:p-1"
                              title="View"
                              onClick={() => openViewProduct(p)}
                            >
                              <Eye className="h-5 w-5" />
                            </button>

                            <button
                              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-slate-100 hover:text-amber-700 sm:min-h-0 sm:min-w-0 sm:p-1"
                              title="Edit"
                              onClick={() => setEditing(p)}
                            >
                              <Pencil className="h-5 w-5" />
                            </button>

                            <button
                              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-slate-100 hover:text-rose-700 sm:min-h-0 sm:min-w-0 sm:p-1"
                              title="Delete"
                              onClick={() => deleteOne(p._id)}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!error && pageData.length === 0 && (
                    <tr>
                      <td
                        colSpan={productLine === "if" ? 10 : 9}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        {productLine === "if"
                          ? "No invoice finance products match your filters."
                          : "No products match your filters."}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="text-center text-sm text-gray-600 sm:text-left">
            Showing <span className="font-semibold">{sortedForDisplay.length}</span>{" "}
            result(s)
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-1 sm:min-w-0 sm:flex-initial sm:justify-end">
            <button
              className="flex h-11 min-w-[44px] items-center justify-center rounded-lg border-2 border-slate-300 bg-white px-3 text-base font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-40 sm:h-9 sm:min-w-0 sm:border sm:px-2 sm:py-1 sm:text-sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              title="Previous"
            >
              «
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(0, 10)
              .map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`flex h-11 min-w-[44px] items-center justify-center rounded-lg border-2 px-2 text-base font-semibold sm:h-9 sm:min-w-[34px] sm:border sm:text-sm ${
                    n === page
                      ? "border-sky-600 bg-sky-500 text-white shadow-sm"
                      : "border-slate-300 bg-white text-slate-800 shadow-sm hover:bg-slate-50"
                  }`}
                >
                  {n}
                </button>
              ))}
            <button
              className="flex h-11 min-w-[44px] items-center justify-center rounded-lg border-2 border-slate-300 bg-white px-3 text-base font-semibold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-40 sm:h-9 sm:min-w-0 sm:border sm:px-2 sm:py-1 sm:text-sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              title="Next"
            >
              »
            </button>
            <span className="ml-0 w-full text-center text-sm text-gray-600 sm:ml-2 sm:w-auto sm:text-left">
              Page <span className="font-semibold">{page}</span> of {totalPages}
            </span>
          </div>
        </div>
      </Card>

      {viewing && (
        <ViewProductModal
          item={viewing}
          productLine={productLine}
          onClose={() => setViewing(null)}
        />
      )}
      {editing && (
        <EditProductModal
          key={editing._id}
          item={editing}
          productLine={productLine}
          onSave={productLine === "if" ? saveEditedIf : saveEdited}
          onClose={() => setEditing(null)}
        />
      )}
      {pendingDelete && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 transition-opacity duration-200 ${
            showDeleteDialog ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className={`w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl transition-all duration-200 ${
              showDeleteDialog ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            <h3 className="text-lg font-semibold text-slate-900">Confirm delete</h3>
            <p className="mt-2 text-sm text-slate-600">
              {pendingDelete.mode === "single"
                ? "Are you sure you want to delete this product?"
                : `Are you sure you want to delete these ${pendingDelete.ids?.length || 0} products?`}
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingStatusChange && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 transition-opacity duration-200 ${
            showStatusDialog ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className={`w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl transition-all duration-200 ${
              showStatusDialog ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            <h3 className="text-lg font-semibold text-slate-900">
              Confirm {pendingStatusChange.action}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to {pendingStatusChange.action} these{" "}
              {pendingStatusChange.ids?.length || 0} products?
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingStatusChange(null)}
                className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmStatusChange}
                className={`px-4 py-2 rounded-lg text-white text-sm font-semibold ${
                  pendingStatusChange.action === "activate"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-amber-600 hover:bg-amber-700"
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {updateSuccessMessage && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 transition-opacity duration-200 ${
            showSuccessDialog ? "opacity-100" : "opacity-0"
          }`}
        >
          <div
            className={`w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl transition-all duration-200 ${
              showSuccessDialog ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            <h3 className="text-lg font-semibold text-slate-900">Success</h3>
            <p className="mt-2 text-sm text-slate-600">{updateSuccessMessage}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setUpdateSuccessMessage("")}
                className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-semibold"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

